/**
 * Hostname-based routing.
 *
 * One SPA bundle serves three audiences, told apart by the browser's
 * hostname rather than a URL path prefix (see docs/multi-tenancy.md):
 *
 *   www.musleempay.com, musleempay.com  → the public marketing page
 *   app.musleempay.com                  → the merchant dashboard
 *   <merchant-slug>.musleempay.com      → that merchant's storefront, at
 *                                          root paths ("/", "/page/:slug",
 *                                          "/product/:slug", "/checkout", …)
 *                                          — no "/store/:slug" prefix, since
 *                                          the subdomain already says which
 *                                          merchant this is.
 *
 * This mirrors the API's own hostname resolution
 * (apps/api/src/plugins/tenant.ts: `extractSubdomain`) — kept here instead of
 * imported from there because the two run in different runtimes (browser vs.
 * Fastify) and the frontend's version has no database lookup, only string
 * matching against the configured root domain.
 */

export const STOREFRONT_ROOT_DOMAIN =
  import.meta.env['VITE_STOREFRONT_ROOT_DOMAIN'] ?? 'musleempay.test';

/** Hostnames that are never a merchant's storefront. */
const RESERVED_SUBDOMAINS = new Set(['www', 'app']);

/**
 * The merchant slug this hostname belongs to, or `null` when the current
 * host is the marketing site, the dashboard, or anything else not under a
 * single-label subdomain of the configured root domain. Plain `localhost`
 * during development always returns `null` too — local dev needs either a
 * wildcard `/etc/hosts` entry (e.g. `127.0.0.1 demo-store.musleempay.test`,
 * matching `VITE_STOREFRONT_ROOT_DOMAIN`) or `vite.config.ts`'s `server.host`
 * reached via a subdomain some other way; see docs/development.md.
 */
export function getMerchantSlugFromHostname(
  hostname: string = window.location.hostname,
): string | null {
  const host = hostname.toLowerCase();

  if (host === STOREFRONT_ROOT_DOMAIN) return null;

  const suffix = `.${STOREFRONT_ROOT_DOMAIN}`;
  if (!host.endsWith(suffix)) return null;

  const label = host.slice(0, -suffix.length);
  // A deeper label (e.g. "a.b.musleempay.com") or an empty one is not a slug.
  if (label === '' || label.includes('.')) return null;
  if (RESERVED_SUBDOMAINS.has(label)) return null;

  return label;
}

/** True when the current hostname is a merchant storefront subdomain. */
export function isStorefrontHost(hostname: string = window.location.hostname): boolean {
  return getMerchantSlugFromHostname(hostname) !== null;
}

/** The absolute URL for a merchant's storefront, for links from the dashboard. */
export function getStorefrontUrl(slug: string, path = ''): string {
  const protocol = window.location.protocol === 'http:' ? 'http:' : 'https:';
  return `${protocol}//${slug}.${STOREFRONT_ROOT_DOMAIN}${path}`;
}

/**
 * True on the public marketing hosts (www and the bare apex).
 *
 * These serve the same bundle as the dashboard, but only the landing page
 * belongs there: signing in or working from the marketing host would put a
 * session on a different origin from the app and split people between two
 * addresses for the same screens.
 */
export function isMarketingHost(hostname: string = window.location.hostname): boolean {
  const host = hostname.toLowerCase();
  return host === STOREFRONT_ROOT_DOMAIN || host === `www.${STOREFRONT_ROOT_DOMAIN}`;
}

/** The same path and query on the dashboard host (app.<root domain>). */
export function getAppUrl(path = '/'): string {
  const protocol = window.location.protocol === 'http:' ? 'http:' : 'https:';
  return `${protocol}//app.${STOREFRONT_ROOT_DOMAIN}${path}`;
}
